import { useCallback, useEffect, useRef, useState } from "react";
import { authFetchJson, authFetchRaw } from "@/lib/api";
import {
  commitTransportError,
  dryRunTransportError,
  importErrorMessage,
  isUsableCommitPayload,
} from "./lexiconImportMessages";
import {
  PROPAGATION_POLL_MS,
  effectivePropagation,
  propagationLabel,
  propagationPercent,
  shouldKeepPolling,
  type Counts,
  type Propagation,
} from "./lexiconPropagation";

/**
 * Read a response body without ever throwing: a 502 from the ingress
 * answers with HTML, and losing the status to a JSON parse error would
 * tell the curator less than the status itself does.
 */
async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

type DryRun = {
  source: string;
  digest: string;
  counts: Counts;
  examples: Record<keyof Counts, string[]>;
};

export function AdminLexiconImport({
  initialEpoch = 0,
  initialCounts = null,
}: {
  initialEpoch?: number;
  initialCounts?: Counts | null;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState<DryRun | null>(null);
  const [committed, setCommitted] = useState<Counts | null>(initialCounts);
  const [epoch, setEpoch] = useState(initialEpoch);
  const [progress, setProgress] = useState<Propagation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Poll the real per-pod state. A countdown would show success while a pod
  // still serves the old lexicon, which is exactly the failure this hides.
  useEffect(() => {
    if (!shouldKeepPolling(epoch, progress)) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const status = await authFetchJson<Propagation>(
          `/api/admin/lexicon/propagation?epoch=${epoch}`,
        );
        if (cancelled) return;
        setProgress(status);
        if (status.state === "pending") {
          timer.current = setTimeout(() => void poll(), PROPAGATION_POLL_MS);
        }
      } catch {
        if (!cancelled) setProgress({ target: epoch, caught_up: 0, total: 0, state: "degraded" });
      }
    };
    void poll();

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
    // `progress` is read only to decide whether polling should continue at
    // all; listing it here would tear down and restart the effect on every
    // poll result, which is the poll restarting itself. `progress?.state`
    // is the one part of it that may legitimately re-arm the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epoch, progress?.state]);

  const runDryRun = useCallback(async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    setDryRun(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await authFetchRaw("/api/admin/lexicon/import/dry-run", {
        method: "POST",
        body,
      });
      const payload = await readJson(response);
      if (!response.ok) {
        setError(importErrorMessage(payload, response.status));
        return;
      }
      setDryRun(payload as DryRun);
    } catch (err) {
      // A dropped connection or a proxy blip. Nothing was written.
      setError(dryRunTransportError(err));
    } finally {
      setBusy(false);
    }
  }, [file]);

  const commit = useCallback(async () => {
    if (!file || !dryRun) return;
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("digest", dryRun.digest);
      const response = await authFetchRaw("/api/admin/lexicon/import/commit", {
        method: "POST",
        body,
      });
      const payload = await readJson(response);
      if (!response.ok) {
        setError(importErrorMessage(payload, response.status));
        return;
      }
      if (!isUsableCommitPayload(payload)) {
        // A 200 the UI cannot read. The write may well have landed, so
        // this says so rather than inviting a blind retry.
        setError(commitTransportError(null));
        return;
      }
      setCommitted(payload.counts as Counts);
      setProgress(null);
      setEpoch(payload.epoch);
      setDryRun(null);
    } catch (err) {
      // The transaction commits before the response is written, so a
      // request that never came back may still have been applied.
      setError(commitTransportError(err));
    } finally {
      setBusy(false);
    }
  }, [file, dryRun]);

  // A zero epoch (Valkey unreachable at commit time) is a real value, not
  // "nothing happened" — there is no epoch to poll `/propagation` for, so
  // this substitutes the degraded status instead of the (never-fetched)
  // real one, so the commit still renders something conclusive.
  const displayProgress = effectivePropagation(epoch, progress);
  const percent = propagationPercent(displayProgress);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            setDryRun(null);
          }}
          className="text-sm"
        />
        <button
          type="button"
          disabled={!file || busy}
          onClick={() => void runDryRun()}
          className="rounded-lg border border-[var(--cs-line)] px-3 py-1.5 text-sm disabled:opacity-40"
        >
          Proefdraaien
        </button>
        <button
          type="button"
          disabled={!dryRun || busy}
          onClick={() => void commit()}
          className="rounded-lg bg-[var(--cs-accent)] px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          Toepassen
        </button>
      </div>

      {error ? (
        <pre className="whitespace-pre-wrap rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {error}
        </pre>
      ) : null}

      {dryRun ? (
        <div className="space-y-2 rounded-lg border border-[var(--cs-line)] p-4 text-sm">
          <p className="font-medium">
            Bron {dryRun.source}: {dryRun.counts.insert} nieuw, {dryRun.counts.update} gewijzigd,{" "}
            {dryRun.counts.deactivate} gedeactiveerd, {dryRun.counts.skipped_collision} overgeslagen.
          </p>
          {(Object.keys(dryRun.examples) as (keyof Counts)[]).map((bucket) =>
            dryRun.examples[bucket].length ? (
              <p key={bucket} className="text-xs text-[var(--cs-ink-muted)]">
                <span className="font-medium">{bucket}:</span>{" "}
                {dryRun.examples[bucket].join(", ")}
              </p>
            ) : null,
          )}
        </div>
      ) : null}

      {committed ? (
        <div className="space-y-2 rounded-lg border border-[var(--cs-line)] p-4 text-sm">
          <p>
            {committed.insert} nieuw, {committed.update} gewijzigd,{" "}
            {committed.deactivate} gedeactiveerd — opgeslagen.
          </p>
          {displayProgress?.state === "degraded" ? (
            <p className="text-amber-700">{propagationLabel(displayProgress)}</p>
          ) : displayProgress?.state === "done" ? (
            <p className="text-emerald-700">{propagationLabel(displayProgress)}</p>
          ) : (
            <>
              <div
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                className="h-2 w-full overflow-hidden rounded-full bg-[var(--cs-line)]"
              >
                <div
                  className="h-full bg-[var(--cs-accent)] transition-[width] duration-500"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p className="text-xs text-[var(--cs-ink-muted)]">{propagationLabel(displayProgress)}</p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
